import React, { useState } from 'react';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Collapse, CircularProgress, Typography, Alert,
  Menu, MenuItem, ListItemIcon as MenuItemIcon,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import {
  ExpandMore, ChevronRight,
  Schema as SchemaIcon,
  TableChart as TableIcon,
  ViewColumn as ColumnIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { listSchemas, listTables, listColumns, executeQuery } from '../../api/connections';

const SchemaTree = React.forwardRef(({ connectionId, onTableClick }, ref) => {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [tables, setTables] = useState({});
  const [columns, setColumns] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextTarget, setContextTarget] = useState(null); // { type: 'schema'|'table', schema, table? }

  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadSchemas = async () => {
    if (loaded) return;
    setLoading(true);
    setError('');
    try {
      const data = await listSchemas(connectionId);
      setSchemas(data.schemas || []);
      setLoaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load schemas');
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    setSchemas([]);
    setTables({});
    setColumns({});
    setExpanded({});
    setLoaded(false);
    setError('');
  };

  React.useImperativeHandle(ref, () => ({ refresh, schemas }));

  // Reset on connection change
  React.useEffect(() => {
    if (connectionId) refresh();
  }, [connectionId]);

  React.useEffect(() => {
    if (connectionId && !loaded) loadSchemas();
  }, [connectionId, loaded]);

  const toggleSchema = async (schema) => {
    const key = schema;
    if (expanded[key]) {
      setExpanded((prev) => ({ ...prev, [key]: false }));
      return;
    }
    if (!tables[key]) {
      try {
        const data = await listTables(connectionId, { schema });
        setTables((prev) => ({ ...prev, [key]: data.tables || [] }));
      } catch (err) {
        setError(`Failed to load tables for ${schema}`);
        return;
      }
    }
    setExpanded((prev) => ({ ...prev, [key]: true }));
  };

  const toggleTable = async (schema, table) => {
    const key = `${schema}.${table}`;
    if (expanded[key]) {
      setExpanded((prev) => ({ ...prev, [key]: false }));
      return;
    }
    if (!columns[key]) {
      try {
        const data = await listColumns(connectionId, table, { schema });
        setColumns((prev) => ({ ...prev, [key]: data.columns || [] }));
      } catch (err) {
        setError(`Failed to load columns for ${table}`);
        return;
      }
    }
    setExpanded((prev) => ({ ...prev, [key]: true }));
  };

  const handleTableClick = (schema, table) => {
    if (onTableClick) onTableClick(schema, table);
  };

  // Context menu handlers
  const handleContextMenu = (e, target) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
    setContextTarget(target);
  };

  const handleContextClose = () => {
    setContextMenu(null);
  };

  const handleDeleteClick = () => {
    handleContextClose();
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contextTarget) return;
    setDeleting(true);
    setError('');
    try {
      if (contextTarget.type === 'schema') {
        await executeQuery(connectionId, `DROP SCHEMA "${contextTarget.schema}" CASCADE`);
      } else if (contextTarget.type === 'table') {
        await executeQuery(connectionId, `DROP TABLE "${contextTarget.schema}"."${contextTarget.table}" CASCADE`);
      }
      setDeleteDialog(false);
      setContextTarget(null);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Delete failed');
      setDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog(false);
    setContextTarget(null);
  };

  const deleteLabel = contextTarget?.type === 'schema'
    ? `schema "${contextTarget?.schema}"`
    : `table "${contextTarget?.schema}"."${contextTarget?.table}"`;

  if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 1 }} onClose={() => setError('')}>{error}</Alert>;

  return (
    <>
      <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
        {schemas.map((schema) => (
          <React.Fragment key={schema}>
            <ListItemButton
              onClick={() => toggleSchema(schema)}
              onContextMenu={(e) => handleContextMenu(e, { type: 'schema', schema })}
            >
              {expanded[schema] ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              <ListItemIcon sx={{ minWidth: 32 }}><SchemaIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={schema} />
            </ListItemButton>
            <Collapse in={expanded[schema]}>
              <List dense disablePadding sx={{ pl: 3 }}>
                {(tables[schema] || []).map((t) => (
                  <React.Fragment key={t.name}>
                    <ListItemButton
                      onClick={() => { toggleTable(schema, t.name); handleTableClick(schema, t.name); }}
                      onContextMenu={(e) => handleContextMenu(e, { type: 'table', schema, table: t.name })}
                    >
                      {expanded[`${schema}.${t.name}`] ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                      <ListItemIcon sx={{ minWidth: 32 }}><TableIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t.name} secondary={t.row_count != null ? `${t.row_count} rows` : null} />
                    </ListItemButton>
                    <Collapse in={expanded[`${schema}.${t.name}`]}>
                      <List dense disablePadding sx={{ pl: 6 }}>
                        {(columns[`${schema}.${t.name}`] || []).map((col) => (
                          <ListItemButton key={col.name} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}><ColumnIcon fontSize="small" /></ListItemIcon>
                            <ListItemText
                              primary={col.name}
                              secondary={`${col.type}${col.nullable ? ', nullable' : ''}`}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
        {schemas.length === 0 && !loading && (
          <Typography variant="body2" sx={{ p: 2, opacity: 0.5 }}>No schemas found</Typography>
        )}
      </List>

      {/* Right-click context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextClose}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleDeleteClick}>
          <MenuItemIcon><DeleteIcon fontSize="small" color="error" /></MenuItemIcon>
          <ListItemText>
            {contextTarget?.type === 'schema' ? 'Drop Schema' : 'Drop Table'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Drop</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to drop {deleteLabel}? This will use <code>CASCADE</code> and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Drop
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default SchemaTree;
